/**
 * 🟥 P23 — BuilderToggle
 * 🟩 P26.5 · TOGGLE GLOBAL PERMANENTE
 *
 * Toggle ON/OFF que controla si DamyxIA puede crear proyectos + código
 * + workspace-actions desde el chat. SIEMPRE visible (no depende de
 * módulo activo). Estado por defecto OFF (regla dura del owner).
 *
 * Persistencia:
 *   - GLOBAL (moduleId=null): localStorage.damyxia_builder_global
 *     ("ON" | "OFF"). Se envía en cada request `/api/chat` como
 *     `builder_toggle` y prevalece sobre el toggle por-módulo.
 *   - POR-MÓDULO (moduleId!=null): PATCH al backend como antes
 *     (retro-compat).
 *
 * UX:
 *   - SIEMPRE visible (aunque no haya módulo activo).
 *   - Actualización optimista: cambio instantáneo en UI + persistencia
 *     en background.
 *   - Primer ON del usuario (global, no por proyecto): modal de
 *     confirmación una única vez (flag `damyxia_builder_modal_seen`).
 *
 * Props:
 *   - moduleId: string | null (ID del módulo activo, opcional)
 *   - enabled: bool (estado actual; en modo global ignorado, se lee de LS)
 *   - onChange: (newEnabled: bool) => void
 *   - language: 'es' | 'en'
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, Unlock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MODAL_SEEN_KEY = 'damyxia_builder_modal_seen';
const GLOBAL_STATE_KEY = 'damyxia_builder_global';  // P26.5

const COPY = {
  es: {
    off: 'Builder OFF',
    on: 'Builder ON',
    tooltipOff: 'Builder desactivado — DamyxIA solo responde en el chat. Toca para activar.',
    tooltipOn: 'Builder activado — DamyxIA puede modificar tu proyecto. Toca para desactivar.',
    modalTitle: 'Activar modo Builder',
    modalBody: 'Al activar Builder, DamyxIA puede modificar los archivos de tu proyecto directamente cuando se lo pidas. Podés desactivarlo en cualquier momento con el mismo toggle.',
    modalConfirm: 'Confirmar y activar',
    modalCancel: 'Cancelar',
    modalDontShow: 'No volver a mostrar este aviso',
    saveError: 'No se pudo cambiar el estado',
  },
  en: {
    off: 'Builder OFF',
    on: 'Builder ON',
    tooltipOff: 'Builder is off — DamyxIA only answers in chat. Tap to enable.',
    tooltipOn: 'Builder is on — DamyxIA can modify your project. Tap to disable.',
    modalTitle: 'Enable Builder mode',
    modalBody: 'When Builder is enabled, DamyxIA can modify your project files directly when you ask it to. You can turn it off any time with the same toggle.',
    modalConfirm: 'Confirm and enable',
    modalCancel: 'Cancel',
    modalDontShow: 'Do not show this again',
    saveError: 'Could not change the status',
  },
};

export function BuilderToggle({ moduleId, enabled, onChange, language = 'es' }) {
  const t = COPY[language === 'en' ? 'en' : 'es'];
  // P26.5 · en modo global (sin moduleId) el estado inicial se lee de LS
  const initialLocal = moduleId
    ? Boolean(enabled)
    : (typeof window !== 'undefined' && window.localStorage.getItem(GLOBAL_STATE_KEY) === 'ON');
  const [local, setLocal] = useState(initialLocal);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Mantener sync cuando el parent cambie el estado (por ej. al abrir otro módulo).
  useEffect(() => {
    if (moduleId) {
      setLocal(Boolean(enabled));
    } else {
      // Modo global: sincroniza cross-tab si LS cambia
      const onStorage = (e) => {
        if (e.key === GLOBAL_STATE_KEY) {
          setLocal(e.newValue === 'ON');
        }
      };
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }
    return undefined;
  }, [enabled, moduleId]);

  // 🟩 P26.5 · Toggle GLOBAL siempre visible (aunque no haya moduleId).

  const persist = async (nextEnabled) => {
    setSaving(true);
    try {
      if (moduleId) {
        // Retro-compat: persistencia por-módulo vía backend
        const token = localStorage.getItem('damyxia_token');
        await axios.patch(
          `${API}/api/damyxia/modules/${moduleId}/builder-toggle`,
          { enabled: nextEnabled },
          token ? { headers: { Authorization: `Bearer ${token}` } } : {},
        );
      } else {
        // 🟩 P26.5 · Modo GLOBAL — persistir en localStorage.
        // El estado se envía en cada request de chat como builder_toggle.
        window.localStorage.setItem(
          GLOBAL_STATE_KEY,
          nextEnabled ? 'ON' : 'OFF',
        );
        // Dispatch evento custom `builder_mode_changed` con payload.
        try {
          window.dispatchEvent(new CustomEvent('builder_mode_changed', {
            detail: {
              state: nextEnabled ? 'on' : 'off',
              user_id: window.localStorage.getItem('damyxia_user_id') || null,
              timestamp: new Date().toISOString(),
            },
          }));
        } catch (_) { /* ignore */ }
      }
      if (onChange) onChange(nextEnabled);
    } catch (e) {
      // Rollback
      setLocal(!nextEnabled);
      toast.error(t.saveError, { description: e?.message || 'PATCH failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleClick = () => {
    if (saving) return;
    const next = !local;
    if (next && localStorage.getItem(MODAL_SEEN_KEY) !== '1') {
      // Primera vez que activa → modal de confirmación.
      setShowModal(true);
      return;
    }
    // Optimistic UI + PATCH background.
    setLocal(next);
    persist(next);
  };

  const confirmModal = (markSeen) => {
    if (markSeen) localStorage.setItem(MODAL_SEEN_KEY, '1');
    setShowModal(false);
    setLocal(true);
    persist(true);
  };

  const btnClass = local
    ? 'bg-emerald-500/90 hover:bg-emerald-500 text-white border-emerald-400'
    : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border-zinc-700';

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={saving}
        data-testid="builder-toggle-btn"
        aria-pressed={local}
        title={local ? t.tooltipOn : t.tooltipOff}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all ${btnClass} ${saving ? 'opacity-60 cursor-wait' : ''}`}
      >
        {local ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
        <span className="hidden sm:inline">{local ? t.on : t.off}</span>
      </button>

      {showModal &&
        createPortal(
          <ConfirmModal
            t={t}
            onCancel={() => setShowModal(false)}
            onConfirm={confirmModal}
          />,
          document.body,
        )}
    </>
  );
}

function ConfirmModal({ t, onCancel, onConfirm }) {
  const [dontShow, setDontShow] = useState(true);
  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="builder-toggle-modal"
    >
      <div
        className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-white mb-2">{t.modalTitle}</h3>
        <p className="text-sm text-zinc-300 leading-relaxed">{t.modalBody}</p>
        <label className="flex items-center gap-2 mt-4 text-xs text-zinc-400 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={dontShow}
            onChange={(e) => setDontShow(e.target.checked)}
            className="accent-emerald-500"
            data-testid="builder-toggle-modal-dontshow"
          />
          {t.modalDontShow}
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm"
            data-testid="builder-toggle-modal-cancel"
          >
            {t.modalCancel}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(dontShow)}
            className="px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-medium"
            data-testid="builder-toggle-modal-confirm"
          >
            {t.modalConfirm}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BuilderToggle;
